import { createUserScopedStorageAtom } from '@heidi/utils/jotai-storage';
import { getDefaultStore } from 'jotai';

/** Must match the Rust `DeviceButtonAction` wire values (pinned by a Rust test). */
export const MAPPED_FUNCTION_ACTIONS = [
  'start_stop_dictating',
  'next_field',
  'previous_field',
] as const;

export type MappedFunctionAction = (typeof MAPPED_FUNCTION_ACTIONS)[number];

export type MappedAction =
  | { type: MappedFunctionAction }
  | { type: 'insert_snippet'; snippetId: string };

export type DeviceButtonMapping = Record<string, MappedAction>;

export type DeviceButtonMappings = Record<string, DeviceButtonMapping>;

export type SnippetPairDraft = {
  buttonId: string;
  snippetId: string;
};

export type MappingDraft = {
  functions: Partial<Record<MappedFunctionAction, string>>;
  snippetPairs: SnippetPairDraft[];
};

export const DEVICE_BUTTON_MAPPINGS_STORAGE_KEY = 'heidi.dictate.deviceButtonMappings:v1';

export const deviceButtonMappingsAtom = createUserScopedStorageAtom<DeviceButtonMappings>(
  DEVICE_BUTTON_MAPPINGS_STORAGE_KEY,
  {}
);

const DEFAULT_START_STOP_BUTTON = 'record';

export const emptySnippetPair = (): SnippetPairDraft => ({ buttonId: '', snippetId: '' });

export const emptyMappingDraft = (): MappingDraft => ({
  functions: {},
  snippetPairs: [emptySnippetPair()],
});

export const isDeviceMappingConfigured = (
  mapping: DeviceButtonMapping | undefined
): boolean => mapping !== undefined && Object.keys(mapping).length > 0;

export const isDictationDeviceConfigured = (deviceKey: string): boolean => {
  const mappings = getDefaultStore().get(deviceButtonMappingsAtom);
  return isDeviceMappingConfigured(mappings[deviceKey]);
};

/**
 * The button that dictates (hold) and toggles hands-free on a device, as the
 * Rust reader decides it (`holds_dictation`): the button mapped to start/stop,
 * else Record unless Record was remapped to another action. `null` when no
 * button triggers dictation.
 */
export const dictationTriggerButton = (
  mapping: DeviceButtonMapping | undefined
): string | null => {
  const entries = Object.entries(mapping ?? {});
  const startStop = entries.find(([, action]) => action.type === 'start_stop_dictating');
  if (startStop) return startStop[0];
  return entries.some(([buttonId]) => buttonId === DEFAULT_START_STOP_BUTTON)
    ? null
    : DEFAULT_START_STOP_BUTTON;
};

const isMappedFunctionAction = (type: string): type is MappedFunctionAction =>
  (MAPPED_FUNCTION_ACTIONS as readonly string[]).includes(type);

/**
 * Storage can hold actions that were since removed (e.g. `toggle_dictation_sound`).
 * Rust rejects the whole payload on an unknown `type`, so drop them per button.
 */
export const sanitizeDeviceMapping = (
  mapping: DeviceButtonMapping
): DeviceButtonMapping => {
  const next: DeviceButtonMapping = {};
  for (const [buttonId, action] of Object.entries(mapping)) {
    if (action.type === 'insert_snippet' || isMappedFunctionAction(action.type)) {
      next[buttonId] = action;
    }
  }
  return next;
};

export const sanitizeDeviceButtonMappings = (
  mappings: DeviceButtonMappings
): DeviceButtonMappings => {
  const next: DeviceButtonMappings = {};
  for (const [deviceKey, mapping] of Object.entries(mappings)) {
    next[deviceKey] = sanitizeDeviceMapping(mapping);
  }
  return next;
};

export const toDeviceMapping = (draft: MappingDraft): DeviceButtonMapping => {
  const mapping: DeviceButtonMapping = {};
  for (const action of Object.keys(draft.functions) as MappedFunctionAction[]) {
    const buttonId = draft.functions[action];
    if (buttonId) mapping[buttonId] = { type: action };
  }
  for (const pair of draft.snippetPairs) {
    if (pair.buttonId && pair.snippetId) {
      mapping[pair.buttonId] = { type: 'insert_snippet', snippetId: pair.snippetId };
    }
  }
  return mapping;
};

export const fromDeviceMapping = (mapping: DeviceButtonMapping): MappingDraft => {
  const functions: MappingDraft['functions'] = {};
  const snippetPairs: SnippetPairDraft[] = [];
  for (const [buttonId, action] of Object.entries(sanitizeDeviceMapping(mapping))) {
    if (action.type === 'insert_snippet') {
      snippetPairs.push({ buttonId, snippetId: action.snippetId });
    } else {
      functions[action.type] = buttonId;
    }
  }
  return {
    functions: {
      start_stop_dictating: DEFAULT_START_STOP_BUTTON,
      ...functions,
    },
    snippetPairs: snippetPairs.length > 0 ? snippetPairs : [emptySnippetPair()],
  };
};

const clearButton = (draft: MappingDraft, buttonId: string): MappingDraft => {
  const functions: MappingDraft['functions'] = { ...draft.functions };
  for (const action of Object.keys(functions) as MappedFunctionAction[]) {
    if (functions[action] === buttonId) delete functions[action];
  }
  return {
    functions,
    snippetPairs: draft.snippetPairs.map((pair) =>
      pair.buttonId === buttonId ? { ...pair, buttonId: '' } : pair
    ),
  };
};

export const assignFunctionButton = (
  draft: MappingDraft,
  action: MappedFunctionAction,
  buttonId: string
): MappingDraft => {
  const next = buttonId ? clearButton(draft, buttonId) : draft;
  const functions = { ...next.functions };
  if (buttonId) functions[action] = buttonId;
  else delete functions[action];
  return { ...next, functions };
};

export const assignSnippetButton = (
  draft: MappingDraft,
  index: number,
  buttonId: string
): MappingDraft => {
  const next = buttonId ? clearButton(draft, buttonId) : draft;
  return {
    ...next,
    snippetPairs: next.snippetPairs.map((pair, pairIndex) =>
      pairIndex === index ? { ...pair, buttonId } : pair
    ),
  };
};

export const assignSnippetId = (
  draft: MappingDraft,
  index: number,
  snippetId: string
): MappingDraft => ({
  ...draft,
  snippetPairs: draft.snippetPairs.map((pair, pairIndex) =>
    pairIndex === index ? { ...pair, snippetId } : pair
  ),
});

export const addSnippetPair = (draft: MappingDraft): MappingDraft => ({
  ...draft,
  snippetPairs: [...draft.snippetPairs, emptySnippetPair()],
});

export const removeSnippetPair = (draft: MappingDraft, index: number): MappingDraft => {
  const snippetPairs = draft.snippetPairs.filter((_, pairIndex) => pairIndex !== index);
  return {
    ...draft,
    snippetPairs: snippetPairs.length > 0 ? snippetPairs : [emptySnippetPair()],
  };
};

export const writeDeviceMapping = (
  deviceKey: string,
  deviceMapping: DeviceButtonMapping
): DeviceButtonMappings => {
  const store = getDefaultStore();
  const current = store.get(deviceButtonMappingsAtom);
  const next: DeviceButtonMappings = { ...current };
  if (Object.keys(deviceMapping).length === 0) {
    delete next[deviceKey];
  } else {
    next[deviceKey] = deviceMapping;
  }
  store.set(deviceButtonMappingsAtom, next);
  return next;
};
